import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../lib/api';
import { Music, Volume2 } from 'lucide-react';

export default function Navbar() {
  const [settings, setSettings] = useState<Record<string, string> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    apiFetch<Record<string, string>>('/api/public/settings').then((res) => {
      if (res.success && res.data) {
        setSettings(res.data);
      }
    });
  }, []);

  useEffect(() => {
    const audio = new Audio('/bg-music.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    audioRef.current = audio;

    const handleFirstInteraction = () => {
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // 自動播放被阻止，等待用戶點擊
      });
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      audio.pause();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, []);

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {});
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-orange-100 shadow-sm">
      <div className="container-max mx-auto px-3 sm:px-4 h-14 sm:h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <img
              src="/images/logo-delicious-express.jpg"
              alt={settings?.orgName || '好餸傳愛'}
              className="h-10 sm:h-12 w-auto rounded-lg object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col">
            <span className="font-bold text-base text-gray-900 leading-tight">
              {settings?.orgName || '好餸傳愛'}
            </span>
            {settings?.orgTagline && (
              <span className="text-xs text-orange-600 font-medium leading-tight">
                {settings.orgTagline}
              </span>
            )}
          </div>
        </a>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* S2C Textile Badge */}
          <div className="hidden md:flex items-center">
            <img
              src="https://s2c-donation-api.jimsbond007.workers.dev/api/public/media/general%2F1777627052462_kexjxfc0.png"
              alt="以學為本 · 連結社區"
              className="h-[72px] w-auto object-contain"
              title="以學為本 · 連結社區"
            />
          </div>

          {/* Music button */}
          <button
            onClick={toggleMusic}
            className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              isPlaying
                ? 'bg-brand-100 text-brand-600'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={isPlaying ? '暫停音樂' : '播放音樂'}
          >
            <span className={`absolute inset-0 rounded-full ${isPlaying ? '' : 'animate-glow-pulse'}`} style={{ boxShadow: isPlaying ? 'none' : '0 0 8px 2px rgba(249, 115, 22, 0.5)' }} />
            {isPlaying ? <Volume2 className="w-4 h-4 relative z-10" /> : <Music className="w-4 h-4 relative z-10 animate-bounce-slow" />}
          </button>
        </div>
      </div>
    </nav>
  );
}
